import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AuthGuard } from '@fry/lib/guards';

import { UsersComponent } from '@fry/components/users/users/users.component';
import { UserListComponent } from '@fry/components/users/user-list/user-list.component';
import { UserEditComponent } from '@fry/components/users/user-edit/user-edit.component';
import { UserEditResolver } from '@fry/components/users/user-edit/user-edit.resolver';
import { UserEditBasicComponent } from './user-edit/user-edit-basic/user-edit-basic.component';
import { UserEditEmailsComponent } from './user-edit/user-edit-emails/user-edit-emails.component';
import { UserEditDetailsComponent } from './user-edit/user-edit-details/user-edit-details.component';
import { UserEditRolesComponent } from './user-edit/user-edit-roles/user-edit-roles.component';
import { UnsavedGuard } from '@fry/components/common/form';
import { UserViewComponent } from './user-view/user-view.component';
import { UserAddComponent } from './user-add/user-add.component';
import { UserDashboardComponent } from './user-dashboard/user-dashboard.component';
import { OrganisationService } from '@fry/lib/organisations';
import { NotifyService } from '@fry/lib/utils';
import { tap } from 'rxjs/operators';
import { ListCredentialsComponent } from "@fry/components/accounts/credential/list-credentials.component";
import { ListMfasComponent } from "@fry/components/accounts/mfa/list-mfas.component";


const userEditRoutes: Routes = [
  {
    path: '',
    redirectTo: 'basic',
    pathMatch: 'full'
  },
  {
    path: 'basic',
    component: UserEditBasicComponent,
    canDeactivate: [
      UnsavedGuard
    ]
  },
  {
    path: 'emails',
    component: UserEditEmailsComponent,
  },
  {
    path: 'details',
    component: UserEditDetailsComponent,
    canDeactivate: [
      UnsavedGuard
    ]
  },
  {
    path: 'roles',
    component: UserEditRolesComponent,
    canDeactivate: [
      UnsavedGuard
    ],
    canActivate: [
      AuthGuard
    ],
    data: {
      requiredPermissions: ['roles.assign'],
      userParamArg: 'user'
    },

  },
];

const routes: Routes = [
  {
    path: '',
    component: UsersComponent,
    canActivate: [
      AuthGuard
    ],
    children: [
      {
        path: '',
        component: UserListComponent,
        canActivate: [
          AuthGuard
        ]
      },
      {
        path: ':user/dashboard',
        component: UserDashboardComponent,
        canActivate: [
          AuthGuard
        ],
        data: {
          requiredPermissions: ['users.view'],
          userParamArg: 'user'
        },
        resolve: {
          user: UserEditResolver
        },
      },
      {
        path: ':user/view',
        component: UserViewComponent,
        canActivate: [
          AuthGuard
        ],
        data: {
          requiredPermissions: ['users.view'],
          requiredOwnPermissions: '',
          userParamArg: 'user'
        },
        resolve: {
          user: UserEditResolver
        },
      },
      {
        path: ':user/edit',
        component: UserEditComponent,
        canActivate: [
          AuthGuard
        ],
        data: {
          requiredPermissions: ['users.edit'],
          userParamArg: 'user'
        },
        resolve: {
          user: UserEditResolver
        },
        children: userEditRoutes
      },
      {
        path: ':user/credentials',
        component: ListCredentialsComponent,
        canActivate: [
          AuthGuard,
        ],
        data: {
          userParamArg: 'user'
        },
        resolve: {
          user: UserEditResolver
        }
      },
      {
        path: ':user/credentials/:credentialUsername/mfas',
        component: ListMfasComponent,
        canActivate: [
          AuthGuard
        ],
        data: {
          userParamArg: 'user'
        },
        resolve: {
          user: UserEditResolver
        }
      },
      {
        path: 'add',
        component: UserAddComponent,
        canActivate: [
          AuthGuard,
          'canCreateUserGuard',
        ],
        data: {
          requiredPermissions: ['users.create'],
        }
      }
    ]
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
  providers: [
    UserEditResolver,
    {
      provide: 'canCreateUserGuard',
      useFactory: (currentOrg: OrganisationService, notify: NotifyService) => {
        return () => currentOrg.canCreateUsers().pipe(
          tap(res => {
            if (!res) {
              notify.error($localize `Creating users is not allowed`);
            }
          })
        );
      },
      deps: [OrganisationService, NotifyService]
    },
  ]
})
export class UsersRoutingModule { }
